import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'
import { addToCart, setCartItems, type CartItem } from '#bv/lib/cart'
import { useProductStock } from '#bv/lib/useProductStock'

type Props = {
  productId: number
  name: string
  unitPrice: number
  /** Tighter layout for sticky mobile bar */
  compact?: boolean
}

const pillBase =
  'inline-flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/45 disabled:cursor-not-allowed disabled:opacity-45 active:scale-[0.98]'

export function ProductPurchaseActions({ productId, name, unitPrice, compact }: Props) {
  const navigate = useNavigate()
  const { inStock } = useProductStock()
  const productInStock = inStock(productId)
  const [addedFlash, setAddedFlash] = useState(false)

  const lineItem = (): CartItem => ({
    productId,
    name,
    unitPrice,
    quantity: 1,
  })

  const onAddToCart = () => {
    if (!productInStock) return
    addToCart({ productId, name, unitPrice }, 1)
    setAddedFlash(true)
    window.setTimeout(() => setAddedFlash(false), 1600)
  }

  const onBuyNow = () => {
    if (!productInStock) return
    setCartItems([lineItem()])
    void navigate({ to: '/cart', search: { checkout: '1' } })
  }

  const pad = compact ? 'py-2.5 text-xs sm:py-3 sm:text-sm' : 'py-3 text-sm sm:min-h-[2.875rem] sm:text-[0.9375rem]'
  const rowClass = compact ? 'flex-row gap-2' : 'flex-col gap-2.5 sm:flex-row sm:gap-3'

  if (!productInStock) {
    return (
      <button
        type="button"
        disabled
        className={`${pillBase} ${pad} border border-white/12 bg-white/[0.03] text-[#5a6d8a]`}
      >
        Out of stock
      </button>
    )
  }

  return (
    <div className={`flex w-full ${compact ? 'flex-col gap-1.5' : 'flex-col gap-2'}`}>
      <div className={`flex w-full ${rowClass}`}>
        <button
          type="button"
          onClick={onBuyNow}
          className={`btn-primary ${pillBase} ${pad} flex-1 shadow-[0_6px_28px_rgba(0, 191, 255,0.35)] hover:shadow-[0_10px_36px_rgba(0, 191, 255,0.45)]`}
        >
          Buy now
        </button>
        <button
          type="button"
          onClick={onAddToCart}
          className={`${pillBase} ${pad} flex-1 border border-white/20 bg-white/[0.03] text-[#f0f6ff] backdrop-blur-sm hover:border-[rgba(0, 191, 255,0.45)] hover:bg-[rgba(0, 191, 255,0.1)] ${
            addedFlash ? 'ring-2 ring-emerald-400/45' : ''
          }`}
        >
          <ShoppingCart className="h-[1.1em] w-[1.1em] shrink-0 opacity-90" strokeWidth={2} aria-hidden />
          {addedFlash ? 'Added' : 'Add to cart'}
        </button>
      </div>
      {!compact ? (
        <p className="text-center text-[0.7rem] leading-relaxed sm:text-xs" style={{ color: '#5a6d8a' }}>
          Secure checkout — card totals may include processor fees.
        </p>
      ) : null}
    </div>
  )
}
